import { BUTTON } from '@/src/constants/itinerary/addons'
import { SelectorConfig } from '@/src/types/itinerary'
import { TranslationFunction } from '@/src/types/translations'
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material'
import { Box, Button, Card, CardMedia, IconButton, Typography } from '@mui/material'

const AddonsItemCard = ({
    imageUrl,
    description,
    price,
    itemIndicator,
    selectorConfig,
    onAddClick,
    onRemoveClick,
    t
}: {
    imageUrl?: string | null
    description: string
    price: string
    itemIndicator: {
        show: boolean
        color: string
    }
    selectorConfig: SelectorConfig
    onAddClick: () => void
    onRemoveClick: () => void
    t: TranslationFunction<'itineraryPage.addons'>
}) => {
    const { type, isSelected, quantity, totalSelections, minQuantity, maxQuantity } = selectorConfig
    const renderActionButton = () => {
        if (type === BUTTON) {
            // Single passenger: ADD/REMOVE toggle
            return (
                <Button
                    variant={isSelected ? 'contained' : 'outlined'}
                    onClick={isSelected ? onRemoveClick : onAddClick}
                    disabled={!isSelected && totalSelections >= maxQuantity}
                    size="medium"
                    sx={{
                        py: 0.5,
                        px: 2.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        flexShrink: 0,
                        minWidth: 90,
                        width: 90,
                        height: 36,
                        ...(isSelected
                            ? {
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  '&:hover': {
                                      bgcolor: { md: 'primary.dark' }
                                  }
                              }
                            : {
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                  '&:hover': {
                                      bgcolor: { md: 'primary.main' },
                                      color: { md: 'white' },
                                      borderColor: { md: 'primary.main' }
                                  }
                              })
                    }}
                >
                    {isSelected ? t('Remove') : t('Add')}
                </Button>
            )
        } else {
            // Multiple passengers: Counter or ADD button
            if (!isSelected) {
                // Show ADD button initially
                return (
                    <Button
                        variant="outlined"
                        onClick={onAddClick}
                        size="medium"
                        disabled={totalSelections >= maxQuantity}
                        sx={{
                            py: 0.5,
                            px: 2.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            flexShrink: 0,
                            minWidth: 90,
                            width: 90,
                            height: 36,
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                                bgcolor: 'primary.main',
                                color: 'white',
                                borderColor: 'primary.main'
                            }
                        }}
                    >
                        ADD
                    </Button>
                )
            } else {
                // Show counter
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid',
                            borderColor: 'primary.main',
                            borderRadius: 1,
                            flexShrink: 0,
                            width: 90,
                            minWidth: 90,
                            height: 36
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={onRemoveClick}
                            disabled={totalSelections <= minQuantity}
                            sx={{
                                borderRadius: 0,
                                color: 'primary.main',
                                flex: 1,
                                height: '100%'
                            }}
                        >
                            <RemoveIcon fontSize="small" />
                        </IconButton>

                        <Typography
                            variant="body2"
                            sx={{
                                px: 1,
                                py: 0.5,
                                fontWeight: 600,
                                color: 'primary.main',
                                minWidth: 24,
                                textAlign: 'center',
                                flex: 1
                            }}
                        >
                            {quantity}
                        </Typography>

                        <IconButton
                            size="small"
                            onClick={onAddClick}
                            disabled={totalSelections >= maxQuantity}
                            sx={{
                                borderRadius: 0,
                                color: 'primary.main',
                                flex: 1,
                                height: '100%',
                                '&.Mui-disabled': {
                                    color: 'grey.400'
                                }
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )
            }
        }
    }

    return (
        <Card
            sx={{
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2,
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
            elevation={0}
        >
            <Box
                sx={{
                    display: 'flex',
                    p: 2,
                    height: '100%',
                    gap: 2
                }}
            >
                {/* Left Column: Image spanning full height */}
                {imageUrl && (
                    <Box
                        sx={{
                            position: 'relative',
                            width: 80,
                            height: 80,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            overflow: 'hidden',
                            flexShrink: 0
                        }}
                    >
                        <CardMedia
                            component="img"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                            image={imageUrl}
                            alt={description}
                        />

                        {itemIndicator.show && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 4,
                                    left: 4,
                                    width: 12,
                                    height: 12,
                                    bgcolor: itemIndicator.color,
                                    borderRadius: '50%'
                                }}
                            />
                        )}
                    </Box>
                )}

                {/* Right Column: Text and Price/Button */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0
                    }}
                >
                    {/* Text Content - Top of right column */}
                    <Box sx={{ flex: 1, mb: 0.5 }}>
                        <Typography
                            variant="body1"
                            component="h3"
                            sx={{
                                fontWeight: 400,
                                color: 'text.primary',
                                lineHeight: 1.3
                            }}
                        >
                            {description}
                        </Typography>
                    </Box>

                    {/* Price and Button Row - Bottom of right column */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Typography
                            variant="body1"
                            component="div"
                            sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                fontSize: '1rem'
                            }}
                        >
                            {price}
                        </Typography>

                        {renderActionButton()}
                    </Box>
                </Box>
            </Box>
        </Card>
    )
}

export default AddonsItemCard
